"use client";

import { useState, useCallback, useSyncExternalStore } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Photo } from "@/types/listing";
import { getPlaceholderImage, cn } from "@/lib/utils";
import { photoSrc } from "@/lib/photos";
import { cardPhotoLoader } from "@/lib/imageLoader";
import { listingViewTransitionName } from "@/lib/listingViewTransition";

interface PhotoCarouselProps {
  photos: Photo[];
  listingId: string;
  /** Public MLS identity shared with the detail page transition boundary. */
  mlsListingId?: string;
  alt: string;
  sizes?: string;
  aspectRatio?: string;
  /** Prioritize the first image (above-the-fold cards). */
  priority?: boolean;
  /**
   * The listing has photos but this surface hasn't received them yet (the
   * search index lags the database on brand-new homes, and the map's light
   * projection omits photos outright). Shimmers *over* the placeholder while
   * the real set is on its way.
   */
  pending?: boolean;
  /** Omit the global boundary when this card is a repeated rail copy. */
  viewTransitionEnabled?: boolean;
}

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const subscribeReducedMotion = (onChange: () => void) => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  const media = window.matchMedia(REDUCED_MOTION_QUERY);
  media.addEventListener?.("change", onChange);
  return () => media.removeEventListener?.("change", onChange);
};
const reducedMotionSnapshot = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia(REDUCED_MOTION_QUERY).matches;
const serverReducedMotionSnapshot = () => false;

/**
 * When the carousel's controls are visible.
 *
 * They were `opacity-0` revealed only by `group-hover`, which has three
 * consequences that a hover-capable desktop browser cannot show you:
 *
 *   TOUCH. A phone has no hover, so the arrows and dots stayed at opacity 0 for
 *   the entire session. A buyer on a phone saw a photo with no visible way to
 *   reach the next one — while the controls were still there, still tappable,
 *   still in the accessibility tree. Measured on an iPhone 13 viewport.
 *
 *   KEYBOARD. Tabbing moved focus onto an invisible control. That is WCAG 2.4.7
 *   Focus Visible, and it is the worst version of it: the focus ring was
 *   rendered at zero opacity, so the page looked like focus had vanished.
 *
 *   The dots were additionally 6x6 CSS px with no padding — under the 24x24 of
 *   WCAG 2.2 SC 2.5.8 by a factor of four, and small enough on a phone that
 *   aiming at one reliably hits the card link underneath and navigates away.
 *
 * So: hover reveals for a mouse, focus-within reveals for a keyboard, and where
 * neither input exists the controls are simply visible. One constant, because
 * three copies of this rule is how the gradient, the arrows and the dots would
 * drift apart.
 */
const CHROME_REVEAL =
  "opacity-0 transition-opacity group-hover/carousel:opacity-100 " +
  "group-focus-within/carousel:opacity-100 [@media(hover:none)]:opacity-100";

export default function PhotoCarousel({
  photos,
  listingId,
  mlsListingId,
  alt,
  sizes = "(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw",
  aspectRatio = "aspect-[4/3]",
  priority = false,
  pending = false,
  viewTransitionEnabled = true,
}: PhotoCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [imgError, setImgError] = useState<Set<number>>(new Set());
  // Frames that have painted. Until a frame is in here the tile shimmers rather
  // than sitting as a flat grey hole — the optimizer's first encode of a photo
  // costs 100–200 ms even after the W11 fixes, and a card that is *visibly
  // working* reads as much faster than one that is blank for the same time.
  const [loaded, setLoaded] = useState<Set<number>>(new Set());
  const reducedMotion = useSyncExternalStore(
    subscribeReducedMotion,
    reducedMotionSnapshot,
    serverReducedMotionSnapshot
  );
  const markLoaded = useCallback((index: number) => {
    setLoaded((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  }, []);

  const photoList = photos?.length > 0 ? photos : [];
  const hasMultiple = photoList.length > 1;
  const total = photoList.length;
  const transitionName =
    viewTransitionEnabled && !reducedMotion
      ? listingViewTransitionName(mlsListingId, "photo")
      : undefined;
  const transitionProps = transitionName
    ? {
        style: { viewTransitionName: transitionName },
        "data-view-transition-name": transitionName,
        "data-transition-type": "listing-detail",
      }
    : {};

  const goTo = useCallback((index: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(index);
  }, []);

  const prev = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCurrentIndex((i) => (i === 0 ? total - 1 : i - 1));
    },
    [total],
  );

  const next = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setCurrentIndex((i) => (i === total - 1 ? 0 : i + 1));
    },
    [total],
  );

  const getSrc = (index: number): string => {
    if (imgError.has(index) || !photoList[index]) {
      return getPlaceholderImage(listingId);
    }
    return photoSrc(photoList[index], "card");
  };

  const handleError = useCallback(
    (index: number) => {
      setImgError((prev) => new Set(prev).add(index));
      // A failed frame must stop the shimmer too, or the tile spins forever.
      markLoaded(index);
    },
    [markLoaded],
  );

  // No photos to show yet. The placeholder ALWAYS renders — the shimmer goes on
  // top of it while a backfill is outstanding. It used to render *instead* of
  // the image, so a card whose photos never arrived was a blank tile with no
  // `<img>` at all: two of those shipped to the top of the San Jose results on
  // letsgoreal.com, and because the old "Photos coming soon" text was gone, a
  // grep for it passed while the page showed a beige void. Whatever else is
  // true, a card is never nothing.
  if (total === 0) {
    return (
      <div
        {...transitionProps}
        className={cn("relative overflow-hidden bg-raised", aspectRatio)}
      >
        <Image
          src={getPlaceholderImage(listingId)}
          alt={alt}
          fill
          className="object-cover"
          sizes={sizes}
          priority={priority}
        />
        {pending && (
          <span
            aria-hidden
            data-photos-pending
            className="animate-shimmer absolute inset-0 bg-overlay bg-[linear-gradient(110deg,transparent_35%,rgb(32_29_21/7%)_50%,transparent_65%)] bg-[length:200%_100%]"
          />
        )}
      </div>
    );
  }

  return (
    <div
      {...transitionProps}
      className={cn(
        "group/carousel relative overflow-hidden bg-raised",
        aspectRatio,
      )}
      role="group"
      aria-roledescription="carousel"
      aria-label={alt}
      onKeyDown={(e) => {
        if (!hasMultiple) return;
        if (e.key === "ArrowLeft") {
          e.preventDefault();
          e.stopPropagation();
          setCurrentIndex((i) => (i === 0 ? total - 1 : i - 1));
        } else if (e.key === "ArrowRight") {
          e.preventDefault();
          e.stopPropagation();
          setCurrentIndex((i) => (i === total - 1 ? 0 : i + 1));
        }
      }}
    >
      {/* Loading shimmer — sits behind the photo and is revealed while it decodes. */}
      {!loaded.has(currentIndex) && (
        <div
          aria-hidden
          className="animate-shimmer absolute inset-0 bg-overlay bg-[linear-gradient(110deg,transparent_35%,rgb(32_29_21/7%)_50%,transparent_65%)] bg-[length:200%_100%]"
        />
      )}

      {/* Current photo */}
      <Image
        src={getSrc(currentIndex)}
        loader={cardPhotoLoader}
        alt={`${alt} - Photo ${currentIndex + 1} of ${total}`}
        fill
        className={cn(
          "object-cover motion-safe:transition-opacity motion-safe:duration-[300ms]",
          // The LCP candidate never starts transparent: Chrome ignores
          // fully-transparent elements when picking the largest paint, so fading
          // the above-the-fold card in would move its LCP later for no gain. It
          // still gets the shimmer underneath (an undecoded <img> paints
          // nothing), just no fade.
          loaded.has(currentIndex) || (priority && currentIndex === 0)
            ? "opacity-100"
            : "opacity-0",
        )}
        sizes={sizes}
        priority={priority && currentIndex === 0}
        fetchPriority={priority && currentIndex === 0 ? "high" : undefined}
        onLoad={() => markLoaded(currentIndex)}
        onError={() => handleError(currentIndex)}
      />

      {/* subtle bottom gradient for chrome legibility */}
      <div
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/45 to-transparent",
          CHROME_REVEAL,
        )}
      />

      {/* Navigation arrows — visible on hover */}
      {hasMultiple && (
        <>
          <button
            onClick={prev}
            className={cn(
              "glass-blur absolute left-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-ink/55 text-white hover:bg-ink/70",
              CHROME_REVEAL,
            )}
            aria-label="Previous photo"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            onClick={next}
            className={cn(
              "glass-blur absolute right-2 top-1/2 z-10 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-ink/55 text-white hover:bg-ink/70",
              CHROME_REVEAL,
            )}
            aria-label="Next photo"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </>
      )}

      {/* Dot indicators */}
      {hasMultiple && total <= 10 && (
        <div
          className={cn(
            "absolute bottom-0 left-1/2 z-10 flex -translate-x-1/2",
            CHROME_REVEAL,
          )}
        >
          {photoList.map((_, i) => (
            /* The BUTTON is the 24x24 tap target — the WCAG 2.2 SC 2.5.8
               minimum — while the dot a buyer SEES stays 6px inside it. Sizing
               the dot itself to 24px would have drawn a 24px dot. */
            <button
              key={i}
              onClick={(e) => goTo(i, e)}
              className="flex h-6 w-6 items-center justify-center"
              aria-label={`Go to photo ${i + 1}`}
              aria-current={i === currentIndex ? "true" : undefined}
            >
              <span
                aria-hidden
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  i === currentIndex ? "w-3 bg-white" : "w-1.5 bg-white/60",
                )}
              />
            </button>
          ))}
        </div>
      )}

      {/*
        Photo counter.

        The scrim is `ink/65`, not `black/50`, because a scrim's contrast is a
        property of the *photograph*: over a blown-out sky, white 12 px type on
        `black/50` composites to **3.95:1**, under the 4.5:1 AA floor, and the
        favourite heart's `black/40` measured **2.98:1** on the deployed search
        page — below the 3:1 minimum for a control. Same class as the iOS glass
        capsules (2.51:1 on a hero, 1.15:1 over a bright sky), one step less
        severe because the web was already using a real scrim rather than blur.

        At 65% the counter is **5.24:1** against a pure white pixel and the
        heart's 55% is 3.80:1, so neither depends on where the camera was
        pointed; over an ordinary mid-tone photo both are past 10:1 and the
        pill looks much as it did. `ink` rather than `black` because pure black
        is not in this palette. `contrast.spec.ts` re-measures all of it against
        whatever photos are live. (W17 R162)
      */}
      {hasMultiple && (
        <span className="glass-blur absolute bottom-2.5 right-2.5 z-10 rounded-md bg-ink/65 px-2 py-1 text-xs tabular-nums text-white">
          {currentIndex + 1}/{total}
        </span>
      )}
    </div>
  );
}
